import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronRight, MessageCircle } from "lucide-react";
import { ParcelProgress } from "~/components/parcel-progress";
import { formatDay } from "~/lib/format";
import { parcelTitle, routeLabel, statusHeadline, upcomingEta } from "~/lib/parcel-status";
import type { IParcel } from "~/lib/proto";

export function ParcelCard({ parcel, unread = 0, action }: { parcel: IParcel; unread?: number; action?: ReactNode }) {
  const title = parcelTitle(parcel);
  const eta = upcomingEta(parcel);
  const delivered = parcel.status === "DELIVERED";
  const identifier = parcel.trackingNumber && parcel.trackingNumber !== title ? parcel.trackingNumber : routeLabel(parcel);

  return (
    <article className="border-hairline bg-card rounded-xl border">
      <Link to="/parcel/$id" params={{ id: parcel.id! }} className="hover:bg-accent/30 block rounded-xl p-4 transition-colors">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-[16px] font-semibold tracking-[-0.01em]">{title}</h3>
            <p className="text-muted-foreground mt-0.5 truncate text-[13px]">{identifier}</p>
          </div>
          {unread > 0 && (
            <span
              className="bg-primary text-primary-foreground tabular inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-[12px] leading-none font-medium"
              aria-label={`${unread} new ${unread === 1 ? "message" : "messages"}`}
            >
              <MessageCircle className="size-3.5" strokeWidth={2.25} />
              {unread}
            </span>
          )}
        </div>

        {!delivered && <ParcelProgress status={parcel.status} className="mt-4" />}

        <div className="mt-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[15px] font-medium">
              {delivered ? `Delivered ${formatDay(parcel.updatedAt)}` : statusHeadline(parcel)}
            </p>
            {eta && <p className="text-muted-foreground mt-0.5 text-[13px]">Estimated delivery {eta}</p>}
          </div>
          <ChevronRight className="text-muted-foreground size-5 shrink-0" />
        </div>
      </Link>
      {action && <div className="px-4 pb-4">{action}</div>}
    </article>
  );
}
